import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  IconButton,
  Grid,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  Alert,
  MenuItem,
  Avatar,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  Visibility as VisibilityIcon,
  ArrowUpward as MoveUpIcon,
  ArrowDownward as MoveDownIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import RolePlayGame from '../RolePlayGame';
import { createSampleRolePlayGameData } from '../../utils/rolePlayGameUtils';
import Button from './Button';
import TextField from './TextField';
import Card from './Card';
import Select from './Select';
import Accordion from './Accordion';

/**
 * Компонент для визуального редактирования ролевой игры
 * @param {string} content - JSON строка с содержимым ролевой игры
 * @param {function} onChange - Функция обработки изменений
 */
const RolePlayGameEditor = ({ content, onChange }) => {
  const { t } = useTranslation(['lessons']);

  // Начальное состояние данных ролевой игры
  const [gameData, setGameData] = useState({
    character: {
      name: 'Персонаж',
      avatar: 'https://via.placeholder.com/100',
      role: 'Инструктор',
      description: 'Ваш персонаж в ролевой игре',
    },
    scenario: [],
  });

  // Состояние для предпросмотра
  const [previewMode, setPreviewMode] = useState(false);

  // Отслеживаем, изменились ли данные с момента последнего сохранения
  const [isDirty, setIsDirty] = useState(false);

  // Таймер для отложенного сохранения
  const saveTimeoutRef = useRef(null);

  // Загрузка данных из JSON строки при инициализации или обновлении content
  useEffect(() => {
    try {
      if (content) {
        const parsedData =
          typeof content === 'string' ? JSON.parse(content) : content;

        // Проверяем, что данные имеют нужную структуру
        if (parsedData && parsedData.character && parsedData.scenario) {
          setGameData(parsedData);
        } else {
          // Если структура неполная, используем шаблон
          const sampleData = createSampleRolePlayGameData();
          setGameData(sampleData);
        }
      } else {
        // Если данных нет, используем шаблон
        const sampleData = createSampleRolePlayGameData();
        setGameData(sampleData);
      }

      // Сбрасываем флаг изменений при загрузке новых данных
      setIsDirty(false);
    } catch (error) {
      console.error('Ошибка при разборе JSON данных ролевой игры:', error);
      // В случае ошибки, создаем шаблонные данные
      const sampleData = createSampleRolePlayGameData();
      setGameData(sampleData);
    }
  }, [content]);

  
  // Непосредственное сохранение в родительский компонент
  const saveChangesToParent = useCallback(() => {
    if (isDirty) {
      onChange(JSON.stringify(gameData, null, 2));
      setIsDirty(false);
    }
  }, [gameData, isDirty, onChange]);

  // Эффект для отложенного сохранения с debounce
  useEffect(() => {
    if (isDirty) {
      // Очищаем предыдущий таймер
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }

      // Устанавливаем новый таймер для сохранения через 1 секунду
      saveTimeoutRef.current = setTimeout(() => {
        saveChangesToParent();
      }, 1000);
    }

    // Очистка при размонтировании
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [gameData, isDirty, saveChangesToParent]);

  // Обновление данных в локальном state без немедленного сохранения
  const updateGameData = (newGameData) => {
    setGameData(newGameData);
    setIsDirty(true);
  };

  // Обработчики для изменения данных персонажа
  const handleCharacterChange = (field, value) => {
    const newGameData = {
      ...gameData,
      character: {
        ...gameData.character,
        [field]: value,
      },
    };
    updateGameData(newGameData);
  };

  // Добавление нового элемента в сценарий
  const handleAddScenarioItem = (type) => {
    let newItem;

    switch (type) {
      case 'dialog':
        newItem = {
          type: 'dialog',
          text: '',
        };
        break;
      case 'choice':
        newItem = {
          type: 'choice',
          question: '',
          choices: [
            { text: 'Вариант 1', nextScene: gameData.scenario.length + 2 },
            { text: 'Вариант 2', nextScene: gameData.scenario.length + 2 },
          ],
        };
        break;
      case 'input':
        newItem = {
          type: 'input',
          question: '',
          correctAnswer: '',
          feedback: {
            correct: 'Правильно!',
            incorrect: 'Неправильно. Попробуйте еще раз.',
          },
          nextScene: gameData.scenario.length + 1,
        };
        break;
      case 'end':
        newItem = {
          type: 'end',
          text: 'Поздравляем! Вы завершили урок.',
        };
        break;
      default:
        return;
    }

    const newScenario = [...gameData.scenario, newItem];
    const newGameData = {
      ...gameData,
      scenario: newScenario,
    };

    updateGameData(newGameData);
  };

  // Удаление элемента из сценария
  const handleDeleteScenarioItem = (index) => {
    const newScenario = gameData.scenario.filter((_, i) => i !== index);
    const newGameData = {
      ...gameData,
      scenario: newScenario,
    };
    updateGameData(newGameData);
  };

  // Изменение данных элемента сценария
  const handleScenarioItemChange = (index, field, value) => {
    const newScenario = [...gameData.scenario];
    newScenario[index] = {
      ...newScenario[index],
      [field]: value,
    };

    const newGameData = {
      ...gameData,
      scenario: newScenario,
    };

    updateGameData(newGameData);
  };

  // Изменение данных выбора в элементе choice
  const handleChoiceChange = (sceneIndex, choiceIndex, field, value) => {
    const newScenario = [...gameData.scenario];
    newScenario[sceneIndex].choices[choiceIndex] = {
      ...newScenario[sceneIndex].choices[choiceIndex],
      [field]: value,
    };

    const newGameData = {
      ...gameData,
      scenario: newScenario,
    };

    updateGameData(newGameData);
  };

  // Добавление нового выбора в элемент choice
  const handleAddChoice = (sceneIndex) => {
    const newScenario = [...gameData.scenario];
    newScenario[sceneIndex].choices.push({
      text: 'Новый вариант',
      nextScene: gameData.scenario.length,
    });

    const newGameData = {
      ...gameData,
      scenario: newScenario,
    };

    updateGameData(newGameData);
  };

  // Удаление выбора из элемента choice
  const handleDeleteChoice = (sceneIndex, choiceIndex) => {
    const newScenario = [...gameData.scenario];
    newScenario[sceneIndex].choices = newScenario[sceneIndex].choices.filter(
      (_, i) => i !== choiceIndex
    );

    const newGameData = {
      ...gameData,
      scenario: newScenario,
    };

    updateGameData(newGameData);
  };

  // Изменение данных обратной связи в элементе input
  const handleFeedbackChange = (sceneIndex, type, value) => {
    const newScenario = [...gameData.scenario];
    newScenario[sceneIndex].feedback = {
      ...newScenario[sceneIndex].feedback,
      [type]: value,
    };

    const newGameData = {
      ...gameData,
      scenario: newScenario,
    };

    updateGameData(newGameData);
  };

  // Перемещение элемента сценария вверх
  const handleMoveUp = (index) => {
    if (index === 0) return;

    const newScenario = [...gameData.scenario];
    const temp = newScenario[index];
    newScenario[index] = newScenario[index - 1];
    newScenario[index - 1] = temp;

    const newGameData = {
      ...gameData,
      scenario: newScenario,
    };

    updateGameData(newGameData);
  };

  // Перемещение элемента сценария вниз
  const handleMoveDown = (index) => {
    if (index === gameData.scenario.length - 1) return;

    const newScenario = [...gameData.scenario];
    const temp = newScenario[index];
    newScenario[index] = newScenario[index + 1];
    newScenario[index + 1] = temp;

    const newGameData = {
      ...gameData,
      scenario: newScenario,
    };

    updateGameData(newGameData);
  };

  // Валидация данных ролевой игры
  const validateGameData = () => {
    const errors = [];

    // Проверка персонажа
    if (!gameData.character.name)
      errors.push(t('rolePlayGame.editor.errors.characterNameEmpty'));
    if (!gameData.character.role)
      errors.push(t('rolePlayGame.editor.errors.characterRoleEmpty'));

    // Проверка сценария
    if (gameData.scenario.length === 0)
      errors.push(t('rolePlayGame.editor.errors.scenarioEmpty'));

    // Проверка каждого элемента сценария
    gameData.scenario.forEach((scene, index) => {
      if (scene.type === 'dialog' && !scene.text) {
        errors.push(
          t('rolePlayGame.editor.errors.dialogTextEmpty', { index: index + 1 })
        );
      }

      if (scene.type === 'choice') {
        if (!scene.question)
          errors.push(
            t('rolePlayGame.editor.errors.questionEmpty', { index: index + 1 })
          );
        if (!scene.choices || scene.choices.length < 2)
          errors.push(
            t('rolePlayGame.editor.errors.minChoices', { index: index + 1 })
          );

        scene.choices?.forEach((choice, choiceIndex) => {
          if (!choice.text)
            errors.push(
              t('rolePlayGame.editor.errors.choiceTextEmpty', {
                index: index + 1,
                choiceIndex: choiceIndex + 1,
              })
            );
        });
      }

      if (scene.type === 'input') {
        if (!scene.question)
          errors.push(
            t('rolePlayGame.editor.errors.questionEmpty', { index: index + 1 })
          );
        if (!scene.correctAnswer)
          errors.push(
            t('rolePlayGame.editor.errors.correctAnswerEmpty', {
              index: index + 1,
            })
          );
      }

      if (scene.type === 'end' && !scene.text) {
        errors.push(
          t('rolePlayGame.editor.errors.endTextEmpty', { index: index + 1 })
        );
      }
    });

    return {
      isValid: errors.length === 0,
      errors,
    };
  };

  const validation = validateGameData();

  if (previewMode) {
    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6">
            {t('rolePlayGame.editor.preview')}
          </Typography>
        </Box>

        <RolePlayGame
          options={gameData}
          onChange={() => {}}
        />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">{t('rolePlayGame.editor.title')}</Typography>
      </Box>

      {!validation.isValid && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="subtitle2">
            {t('rolePlayGame.editor.errors.title')}
          </Typography>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {validation.errors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          {t('rolePlayGame.editor.characterSettings')}
        </Typography>

        <Grid container spacing={2} alignItems="center">
          <Grid item>
            <Avatar
              src={gameData.character.avatar}
              alt={gameData.character.name}
              sx={{ width: 60, height: 60 }}
            >
              <PersonIcon />
            </Avatar>
          </Grid>
          <Grid item xs={12} sm>
            <TextField
              fullWidth
              label={t('rolePlayGame.editor.characterAvatar')}
              value={gameData.character.avatar || ''}
              onChange={(e) => handleCharacterChange('avatar', e.target.value)}
              margin="normal"
              size="small"
              placeholder="https://via.placeholder.com/100"
            />
          </Grid>
        </Grid>

        <TextField
          fullWidth
          label={t('rolePlayGame.editor.characterName')}
          value={gameData.character.name || ''}
          onChange={(e) => handleCharacterChange('name', e.target.value)}
          margin="normal"
          required
          error={!gameData.character.name}
        />

        <TextField
          fullWidth
          label={t('rolePlayGame.editor.characterRole')}
          value={gameData.character.role || ''}
          onChange={(e) => handleCharacterChange('role', e.target.value)}
          margin="normal"
          required
          error={!gameData.character.role}
        />

        <TextField
          fullWidth
          label={t('rolePlayGame.editor.characterDescription')}
          value={gameData.character.description || ''}
          onChange={(e) => handleCharacterChange('description', e.target.value)}
          margin="normal"
          multiline
          rows={2}
        />
      </Card>

      <Card sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          {t('rolePlayGame.editor.scenario')}
        </Typography>

        {gameData.scenario.length === 0 ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            {t('rolePlayGame.editor.addScenarioItem')}
          </Alert>
        ) : (
          gameData.scenario.map((scene, index) => (
            <Accordion key={index} sx={{ mb: 1 }} elevation={0}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', width: '100%' }}
                >
                  <Typography sx={{ width: '10%', flexShrink: 0 }}>
                    {index + 1}.
                  </Typography>
                  <Typography
                    sx={{
                      width: '20%',
                      flexShrink: 0,
                      color: 'text.secondary',
                    }}
                  >
                    {scene.type === 'dialog'
                      ? t('rolePlayGame.editor.scenarioTypes.dialog')
                      : scene.type === 'choice'
                      ? t('rolePlayGame.editor.scenarioTypes.choice')
                      : scene.type === 'input'
                      ? t('rolePlayGame.editor.scenarioTypes.input')
                      : t('rolePlayGame.editor.scenarioTypes.end')}
                  </Typography>
                  <Typography
                    sx={{
                      flexGrow: 1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {scene.type === 'dialog' || scene.type === 'end'
                      ? scene.text.substring(0, 50) +
                        (scene.text.length > 50 ? '...' : '')
                      : scene.type === 'choice'
                      ? scene.question.substring(0, 50) +
                        (scene.question.length > 50 ? '...' : '')
                      : scene.type === 'input'
                      ? scene.question.substring(0, 50) +
                        (scene.question.length > 50 ? '...' : '')
                      : ''}
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails
                sx={{ bgcolor: (theme) => theme.palette.background.default }}
              >
                <Box
                  sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}
                >
                  <Tooltip title={t('rolePlayGame.editor.moveUp')}>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMoveUp(index);
                      }}
                      disabled={index === 0}
                    >
                      <MoveUpIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t('rolePlayGame.editor.moveDown')}>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMoveDown(index);
                      }}
                      disabled={index === gameData.scenario.length - 1}
                    >
                      <MoveDownIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t('rolePlayGame.editor.delete')}>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteScenarioItem(index);
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </Box>

                <Select
                  value={scene.type}
                  label={t('rolePlayGame.editor.sceneType')}
                  onChange={(e) => {
                    // При смене типа создаем новую сцену нужного типа
                    handleAddScenarioItem(e.target.value);
                    handleDeleteScenarioItem(index);
                  }}
                  sx={{
                    mb: 2,
                  }}
                >
                  <MenuItem value="dialog">
                    {t('rolePlayGame.editor.scenarioTypes.dialog')}
                  </MenuItem>
                  <MenuItem value="choice">
                    {t('rolePlayGame.editor.scenarioTypes.choice')}
                  </MenuItem>
                  <MenuItem value="input">
                    {t('rolePlayGame.editor.scenarioTypes.input')}
                  </MenuItem>
                  <MenuItem value="end">
                    {t('rolePlayGame.editor.scenarioTypes.end')}
                  </MenuItem>
                </Select>

                {scene.type === 'dialog' && (
                  <TextField
                    fullWidth
                    label={t('rolePlayGame.editor.dialogText')}
                    value={scene.text || ''}
                    onChange={(e) =>
                      handleScenarioItemChange(index, 'text', e.target.value)
                    }
                    placeholder="Введите текст диалога здесь..."
                    margin="normal"
                    multiline
                    rows={3}
                    required
                    error={!scene.text}
                  />
                )}

                {scene.type === 'choice' && (
                  <>
                    <TextField
                      fullWidth
                      label={t('rolePlayGame.editor.question')}
                      placeholder="Введите вопрос здесь..."
                      value={scene.question || ''}
                      onChange={(e) =>
                        handleScenarioItemChange(
                          index,
                          'question',
                          e.target.value
                        )
                      }
                      margin="normal"
                      multiline
                      rows={2}
                      required
                      error={!scene.question}
                    />

                    <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                      {t('rolePlayGame.editor.choiceOptions')}
                    </Typography>

                    {scene.choices?.map((choice, choiceIndex) => (
                      <Card key={choiceIndex} sx={{ mb: 1 }}>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 1,
                          }}
                        >
                          <Typography variant="subtitle2">
                            {t('rolePlayGame.editor.choiceOption', {
                              number: choiceIndex + 1,
                            })}
                          </Typography>
                          <Tooltip
                            title={t('rolePlayGame.editor.deleteOption')}
                          >
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() =>
                                handleDeleteChoice(index, choiceIndex)
                              }
                              disabled={scene.choices.length <= 2}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>

                        <TextField
                          fullWidth
                          label={t('rolePlayGame.editor.choiceOption', {
                            number: choiceIndex + 1,
                          })}
                          placeholder="Введите текст варианта здесь..."
                          value={choice.text || ''}
                          onChange={(e) =>
                            handleChoiceChange(
                              index,
                              choiceIndex,
                              'text',
                              e.target.value
                            )
                          }
                          margin="normal"
                          size="small"
                          required
                          error={!choice.text}
                        />

                        <TextField
                          fullWidth
                          label={t('rolePlayGame.editor.goToSceneNumber')}
                          placeholder="Введите номер сцены здесь..."
                          value={choice.nextScene || ''}
                          onChange={(e) => {
                            const value =
                              e.target.value === ''
                                ? ''
                                : Number(e.target.value);
                            handleChoiceChange(
                              index,
                              choiceIndex,
                              'nextScene',
                              value
                            );
                          }}
                          margin="normal"
                          size="small"
                          type="number"
                          InputProps={{
                            inputProps: {
                              min: 0,
                              max: gameData.scenario.length,
                            },
                          }}
                        />
                      </Card>
                    ))}

                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={() => handleAddChoice(index)}
                      sx={{ mt: 1 }}
                    >
                      {t('rolePlayGame.editor.addChoiceOption')}
                    </Button>
                  </>
                )}

                {scene.type === 'input' && (
                  <>
                    <TextField
                      fullWidth
                      label={t('rolePlayGame.editor.question')}
                      placeholder="Введите вопрос здесь..."
                      value={scene.question || ''}
                      onChange={(e) =>
                        handleScenarioItemChange(
                          index,
                          'question',
                          e.target.value
                        )
                      }
                      margin="normal"
                      multiline
                      rows={2}
                      required
                      error={!scene.question}
                    />

                    <TextField
                      fullWidth
                      label={t('rolePlayGame.editor.correctAnswer')}
                      placeholder="Введите правильный ответ здесь..."
                      value={scene.correctAnswer || ''}
                      onChange={(e) =>
                        handleScenarioItemChange(
                          index,
                          'correctAnswer',
                          e.target.value
                        )
                      }
                      margin="normal"
                      required
                      error={!scene.correctAnswer}
                      helperText="Студенты должны ввести этот ответ (регистр не учитывается)"
                    />

                    <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                      {t('rolePlayGame.editor.feedback')}
                    </Typography>

                    <TextField
                      fullWidth
                      label={t('rolePlayGame.editor.correctFeedback')}
                      placeholder="Введите текст обратной связи здесь..."
                      value={scene.feedback?.correct || ''}
                      onChange={(e) =>
                        handleFeedbackChange(index, 'correct', e.target.value)
                      }
                      margin="normal"
                      multiline
                      rows={2}
                    />

                    <TextField
                      fullWidth
                      label={t('rolePlayGame.editor.incorrectFeedback')}
                      placeholder="Введите текст обратной связи здесь..."
                      value={scene.feedback?.incorrect || ''}
                      onChange={(e) =>
                        handleFeedbackChange(index, 'incorrect', e.target.value)
                      }
                      margin="normal"
                      multiline
                      rows={2}
                    />

                    <TextField
                      fullWidth
                      label={t('rolePlayGame.editor.goToSceneNumber')}
                      placeholder="Введите номер сцены здесь..."
                      value={scene.nextScene || ''}
                      onChange={(e) => {
                        const value =
                          e.target.value === '' ? '' : Number(e.target.value);
                        handleScenarioItemChange(index, 'nextScene', value);
                      }}
                      margin="normal"
                      type="number"
                      InputProps={{
                        inputProps: { min: 0, max: gameData.scenario.length },
                      }}
                    />
                  </>
                )}

                {scene.type === 'end' && (
                  <TextField
                    fullWidth
                    label={t('rolePlayGame.editor.endingText')}
                    placeholder="Введите текст завершения здесь..."
                    value={scene.text || ''}
                    onChange={(e) =>
                      handleScenarioItemChange(index, 'text', e.target.value)
                    }
                    margin="normal"
                    multiline
                    rows={3}
                    required
                    error={!scene.text}
                  />
                )}
              </AccordionDetails>
            </Accordion>
          ))
        )}

        <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => handleAddScenarioItem('dialog')}
            sx={{ mr: 1 }}
          >
            {t('rolePlayGame.editor.addDialog')}
          </Button>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => handleAddScenarioItem('choice')}
            sx={{ mr: 1 }}
          >
            {t('rolePlayGame.editor.addChoice')}
          </Button>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => handleAddScenarioItem('input')}
            sx={{ mr: 1 }}
          >
            {t('rolePlayGame.editor.addInput')}
          </Button>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => handleAddScenarioItem('end')}
          >
            {t('rolePlayGame.editor.addEnding')}
          </Button>
        </Box>
      </Card>

      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button
          variant="outlined"
          color="primary"
          onClick={() => setPreviewMode(true)}
          disabled={!validation.isValid}
        >
          {t('rolePlayGame.editor.preview')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={saveChangesToParent}
          disabled={!isDirty}
        >
          {t('rolePlayGame.editor.saveChanges')}
        </Button>
      </Box>
    </Box>
  );
};

export default RolePlayGameEditor;
